import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { sort } from "fast-sort";
import Send from "../../core/communication";
import { DataSheetGridScreen } from "../../screen/DatasheetgridScreen"
import { useProjects, useScope } from "../SelectProjectCompanyContainer";
import { formatDataOrganizations } from "../DashboardContainer";
import { formattedProjects } from "../../components/Subheader";
import { removeDuplicatedArray } from "../../core/utils/manipulateArrays";
import { toRemoveDoubleQuoteArr, useDWOptions } from "../../core/utils/hooks/useDwOptions";
import { customToast } from "../../components/Toast";
import { useFormProject } from "../../core/utils/hooks/useFormProject";

const getScopeOptions = (projects, companies, organizations, scopeType) => {
  if(!scopeType || scopeType === 'organization') {
    return organizations;
  } else if(scopeType === 'company') {
    return companies;
  } else {
    return projects;
  }
}

export default function DataSheetGrid() {
  const { scope, scopeType } = useScope();
  const [data, setData] = useState(null);
  const [originalData, setOriginalData] = useState({});
  const { projects } = useProjects();
  const { organizations } = useSelector((state) => state.app);
  const [filterData, setFilterData] = useState({ forms: [] });
  const [loading, setIsLoading] = useState(false);
  const [dw, reload ,loadingDw] = useDWOptions(filterData);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [rowsToUpdate, setRowsToUpdate] = useState([]);
  const [simpleFilter, setSimpleFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [rowsToDelete, setRowsToDelete] = useState([]);
  const [cellOnFocus, setCellOnFocus] = useState({ text: null, select: null });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [onlyEmptyAnswers, setOnlyEmptyAnswer] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const { forms, steps, stepQuestions, setSelectedFormId, selectedFormId, setSelectedStepId } = useFormProject(
    filterData?.scope && filterData?.scope[0]?.value,
    filterData?.filterType?.value,
  );
  const [settings, setSettings] = useState({
    orderByColumn: { label: "Atendimentos", value: "id" },
    orderByType: { label: "Ordem Crescente", value: "asc" },
  });

  useEffect(() => {
    if(scope && scopeType) {
      setFilterData({
        ...filterData,
        filterType: {
          value: scopeType,
          label: scopeType == 'project' ? "Projetos" : scopeType == 'company' ? "Empresas" : "Organizações"
        },
        scope: [{ value: scope.id, label: scope?.description, scopeType }]
      });
    }
  }, []);

  useEffect(() => {
    if(filterData?.forms && filterData?.forms.length) {
      setSelectedFormId(filterData?.forms);
    } else {
      setSelectedFormId(null);
    }

    return () => setSelectedFormId(null);
  }, [filterData?.forms])

  useEffect(() => {
    if(filterData?.steps && filterData?.steps.length) {
      setSelectedStepId(filterData?.steps);
    } else {
      setSelectedStepId(null);
    }

    return () => setSelectedStepId(null);
  }, [filterData?.steps])


  const handleEmptyAnswers = () => setOnlyEmptyAnswer(!onlyEmptyAnswers);

  const handleOpenSettings = () => setIsSettingsOpen(true);
  
  const handleCloseSettings = () => setIsSettingsOpen(false);

  const handleOpenFilter = () => setIsFilterOpen(true);

  const handleCloseFilter = () => setIsFilterOpen(false);

  const handleChangeSettings = (key) => {
    return (value) => {
      setSettings(current => ({...current, [key]: value}));
    }
  }

  const applySimpleFilter = () => {
    if(!data) 
      return null;

    let dataToReturn = data;

    const getFilteredSearch = (dataToFilter) => {
      return dataToFilter.filter(row => {
        if(
          (row.name && row.name.toLowerCase().includes(simpleFilter.toLowerCase())) ||
          (row.question && row.question.toLowerCase().includes(simpleFilter.toLowerCase())) || 
          (row.answer && row.answer.toLowerCase().includes(simpleFilter.toLowerCase()))
        ) {
          return row;
        }
      }).filter(row => row);
    }

    const getIsOriginalEmpty = (row, type = 'text') => {
      const originalRow = originalData[type].find(item => item.fsqId === row.fsqId);

      if(originalRow) {
        return !originalRow.answer || originalRow.answer === '∅' || originalRow.answer === "Ø";
      }
    }

    if(onlyEmptyAnswers) {
      dataToReturn = {
        text: dataToReturn.text.filter(row => getIsOriginalEmpty(row)),
        select: dataToReturn.select.filter(row => getIsOriginalEmpty(row, 'select')),
      }
    }

    if(simpleFilter) {
      dataToReturn =  {
        text: getFilteredSearch(dataToReturn['text']),
        select: getFilteredSearch(dataToReturn['select']),
      }
    }

    if(settings.orderByColumn && settings.orderByType) {
      if(settings.orderByType?.value == 'asc') {
        if(settings.orderByColumn?.value === 'date') {
          dataToReturn = {
            text: dataToReturn.text.sort((a, b) => moment(a.date, 'DD/MM/YYYY HH:mm') - moment(b.date, 'DD/MM/YYYY HH:mm')),
            select: dataToReturn.select.sort((a, b) => moment(a.date, 'DD/MM/YYYY HH:mm') - moment(b.date, 'DD/MM/YYYY HH:mm')),
          };
        } else {
          dataToReturn = {
            text: sort(dataToReturn.text).asc(row => row[settings.orderByColumn?.value]),
            select: sort(dataToReturn.select).asc(row => row[settings.orderByColumn?.value])
          };
        }
      } else {
        if(settings.orderByColumn?.value === 'date') {
          dataToReturn = {
            text: dataToReturn.text.sort((a, b) => moment(b.date, 'DD/MM/YYYY HH:mm') - moment(a.date, 'DD/MM/YYYY HH:mm')),
            select: dataToReturn.select.sort((a, b) => moment(b.date, 'DD/MM/YYYY HH:mm') - moment(a.date, 'DD/MM/YYYY HH:mm')),
          };
        } else {
          dataToReturn = {
            text: sort(dataToReturn.text).desc(row => row[settings.orderByColumn?.value]),
            select: sort(dataToReturn.select).desc(row => row[settings.orderByColumn?.value])
          };
        }
      }
    }

    return dataToReturn;
  }

  const handleSelectUsers = (newData) => {
    if(newData) {
      let newSelectedUsers = [];

      if(newData.text && newData.text.length) {
        newData.text.map(row => {
          if(row.userSelect) {
            const userIsSelected = newSelectedUsers.findIndex(user => user.userId === row.userId);

            if(userIsSelected == -1) {
              newSelectedUsers.push({
                ...row,
                questions: [
                  { questionId: row.formQuestionId, question: row.question, step: row.step }
                ]
              });
            } else if(newSelectedUsers[userIsSelected]?.formId === row?.formId) {
              const hasQuestion = newSelectedUsers[userIsSelected].questions.find(item => item.questionId === row.formQuestionId);

              if(!hasQuestion) {
                newSelectedUsers[userIsSelected].questions.push(
                  { questionId: row.formQuestionId, question: row.question, step: row.step }
                )
              }
            }
          }
        });
      }

      if(newData.select && newData.select.length) {
        newData.select.map(row => {
          if(row.userSelect) {
            const userIsSelected = newSelectedUsers.findIndex(user => user.userId === row.userId);

            if(userIsSelected == -1) {
              newSelectedUsers.push({
                ...row,
                questions: [
                  { questionId: row.formQuestionId, question: row.question, step: row.step }
                ]
              });
            } else if(newSelectedUsers[userIsSelected]?.formId === row?.formId) {
              const hasQuestion = newSelectedUsers[userIsSelected].questions.find(item => item.questionId === row.formQuestionId);

              if(!hasQuestion) {
                newSelectedUsers[userIsSelected].questions.push(
                  { questionId: row.formQuestionId, question: row.question, step: row.step }
                )
              }
            }
          }
        });
      }

      setSelectedUsers(newSelectedUsers);
    }
  }

  const handleSelectAll = () => {
    const filteredGeneralData = applySimpleFilter();
    let dataCopy = data;

    const getCurrentStatus = (dataToVerify, filteredData) => {
      const checked = !selectAll; // { ...row, userSelect: !selectAll }

      return dataToVerify.map(row => {
        const currentIsFiltered = filteredData.find(item => item.fsqId === row.fsqId);
        
        if(currentIsFiltered && checked) {
          return { ...row, userSelect: true };
        }

        return { ...row, userSelect: false };
      });
    }

    dataCopy = {
      text: getCurrentStatus(dataCopy.text, filteredGeneralData.text),
      select: getCurrentStatus(dataCopy.select, filteredGeneralData.select)
    };
    
    handleSelectUsers(dataCopy);
    setData(dataCopy);
    setSelectAll(!selectAll);
  }

  const handleChange = (rows, updated, type = 'text') => {
    let newValuesToUpdate = [];

    if(type === 'text' || type === 'text-select') {
      type = type === 'text-select' ? 'select' : 'text';

      updated.map(item => {
        if(item.fromRowIndex || item.fromRowIndex === 0) {
          const existUpdate = newValuesToUpdate && newValuesToUpdate.find(row => row.fsqId === rows[item.fromRowIndex]?.fsqId);
  
          if(!existUpdate) {
            newValuesToUpdate = [
              {
                ...rows[item.fromRowIndex],
                oldAnswer: originalData[type].find(row => row.fsqId === rows[item.fromRowIndex].fsqId)?.answer,
                newAnswer: rows[item.fromRowIndex].answer,
                updatedData: moment().format('DD/MM/YYYY HH:mm:ss')
              },
              ...newValuesToUpdate
            ];
          } else {
            if(rows[item.fromRowIndex].answer === originalData[type][item.fromRowIndex]?.answer) {
              newValuesToUpdate = [...newValuesToUpdate.filter(row => row.fsqId !== rows[item.fromRowIndex]?.fsqId)];
            } else {
              newValuesToUpdate = [
                {
                  ...rows[item.fromRowIndex],
                  oldAnswer: originalData[type].find(row => row.fsqId === rows[item.fromRowIndex].fsqId)?.answer,
                  newAnswer: rows[item.fromRowIndex].answer,
                  updatedData: moment().format('DD/MM/YYYY HH:mm:ss')
                },
                ...newValuesToUpdate.filter(row => row.fsqId !== rows[item.fromRowIndex]?.fsqId)
              ];
            }
          }
        }
      });
    } else {
      newValuesToUpdate = [
        {
          ...rows,
          oldAnswer: originalData[type].find(row => row.fsqId === rows.fsqId)?.answer,
          newAnswer: rows.answer,
          updatedData: moment().format('DD/MM/YYYY HH:mm:ss')
        },
        ...newValuesToUpdate.filter(row => row.fsqId !== rows.fsqId)
      ];
    }

    let newData = data;
    newData = {
      ...newData,
      [type]: newData[type].map(row => {
        const existRow = Array.isArray(rows) ? rows.find(item => item.fsqId === row.fsqId) : (row.fsqId === rows.fsqId ? rows : null);
  
        if(existRow)
          return existRow;
  
        return row;
      })
    }

    setRowsToUpdate(current => [
      ...newValuesToUpdate
      .filter(row => row.newAnswer !== row.oldAnswer),
      ...current
      .filter(row => !newValuesToUpdate.find(item => item.fsqId === row.fsqId))
    ]);
    setData(newData);
    handleSelectUsers(newData);
  }

  const handleChangeDeleteRow = (rowData, type = 'text') => {
    if(rowData && rowData.userSelect) {
      customToast("Você não pode remover uma linha selecionada");
      return;
    }

    setData(current => ({
        ...current,
        [type]: current[type].filter(row => row?.fsqId !== rowData.fsqId)
        .map(row => {
          const hasUpdated = rowsToUpdate.find(item => item?.fsqId === row?.fsqId);

          if(hasUpdated) {
            return { ...row, answer: hasUpdated?.newAnswer || hasUpdated?.answer }
          }
    
          return row;
        })
      })
    );
    setRowsToUpdate(current => current.filter(row => row.fsqId !== rowData.fsqId));
    setRowsToDelete((current) => [rowData, ...current]);
  } 

  const handleRecoveryRow = (rowData) => {
    let isUpdatedRow = originalData.text.find(item => item.fsqId === rowData.fsqId)
    let rowType = 'text';

    if(!isUpdatedRow) {
      isUpdatedRow = originalData.select.find(item => item.fsqId === rowData.fsqId);
      rowType = isUpdatedRow ? 'select' : 'text';
    }

    isUpdatedRow = isUpdatedRow?.answer !== rowData.answer ?
      {
        ...rowData,
        answer: isUpdatedRow?.answer,
        newAnswer: rowData.answer,
        oldAnswer: isUpdatedRow?.answer,
        updatedData: moment().format('DD/MM/YYYY HH:mm:ss')
      } 
    : null;

    setRowsToDelete(current => current.filter(row => row.fsqId !== rowData.fsqId));
    isUpdatedRow && setRowsToUpdate(current => [isUpdatedRow, ...current]);
    setData(current => ({
      ...current,
      [rowType]: [rowData, ...current[rowType]]
    }));
  }

  const handleSimpleFilter = (event) => setSimpleFilter(event.target.value);

  const handleChangeFilter = (field) => {
    return (value) => {
      if(field == 'scopeType') {
        setFilterData((values) =>({
          ...values,
          [field]: value,
          scope: null,
          users: [],
          forms: [],
          questions: []
        }));

        return;
      }

      setFilterData((values) =>({ ...values, [field]: value }));
    }
  }

  const handleCancelUpdates = () => {
    setRowsToUpdate([]);
    setRowsToDelete([]);
    setData(originalData);
  }

  const handleSearchData = async () => {
    try {
      setIsLoading(true);
      
      let selectedScopeType = filterData?.filterType?.value;
      let projects = toRemoveDoubleQuoteArr(filterData?.scope);

      const response = await Send.post(`/assistance/by-advanced-filter?projects=${projects}&scopeType=${selectedScopeType}`, filterData);
      
      if(!response || (!response.text.length && !response.select.length))
        customToast("Não há dados para esse filtro.", 'info');

      const newData = {
        text: sort(response.text).asc(u => u.id),
        select: sort(response.select).asc(u => u.id)
      };

      setOriginalData(newData);
      setData(newData);
      handleCloseFilter();
    } catch(error) {
      console.log("Error", error);
      customToast("Erro ao buscar atendimentos", error);
    }
    setIsLoading(false);
  }

  const handleSaveChanges = async () => {
    setIsLoading(true);
    try {
      const dataToUpdate = {
        rowsToUpdate,
        rowsToDelete
      };

      const response = await Send.put('/assistance/form-submission-question-list', dataToUpdate);

      if(!response || !response.status || response.status === 'ERROR') 
        throw new Error('Erro ao atualizar dados');

      await handleSearchData();
      handleCancelUpdates();
      customToast('Dados Atualizados com sucesso.', 'success');
    } catch(error) {
      console.log("Error -> ", error);
      customToast('Erro ao salvar alterações. Tente novamente ou entre em contato com o suporte.', 'error');
    }

    setIsLoading(false);
  }

  return <DataSheetGridScreen
    {...{
      data: applySimpleFilter(),
      handleChange,
      handleCloseFilter,
      handleOpenFilter,
      isFilterOpen,
      handleChangeFilter,
      filterData,
      handleSearchData,
      rowsToUpdate,
      handleSimpleFilter,
      handleCancelUpdates,
      selectedUsers,
      handleChangeDeleteRow,
      rowsToDelete,
      handleRecoveryRow,
      handleOpenSettings,
      handleCloseSettings,
      isSettingsOpen,
      handleChangeSettings,
      settings,
      cellOnFocus,
      setCellOnFocus,
      handleSaveChanges,
      handleEmptyAnswers,
      onlyEmptyAnswers,
      handleSelectAll,
      selectAll,
      loading: (loading || loadingDw) ? true : false,
      scopeOptions: getScopeOptions(
        formattedProjects(projects),
        formatDataOrganizations(organizations, "companies"),
        formatDataOrganizations(organizations),
        filterData?.filterType?.value
      ),
      dw: {
        ...dw,
        forms: forms.map(row => ({ value: row?.id, label: row?.title })).filter(row => row && row.value),
        steps,
        questions: removeDuplicatedArray(
          dw?.questions?.filter(row => row.Formulario && filterData?.forms?.find(item => item?.value === row?.Formulario))?.flat(),
          "label"
        ) || [],
      }
    }}
  />
}
